import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of, from, timer, race } from 'rxjs';
import { 
  map, 
  catchError, 
  exhaustMap, 
  switchMap, 
  tap, 
  mergeMap,
  take,
  filter,
  withLatestFrom
} from 'rxjs/operators';
import { Hub, HubCapsule, HubPayload } from '@aws-amplify/core';
import type { AuthUser } from '@aws-amplify/auth';

import { AuthActions } from './auth.actions';
import { AppState } from '../index';
import { selectLastProcessedUserId, selectIsAuthenticated, selectInitialAuthCheckCompleted } from './auth.selectors';
import { selectUrl, selectIsPublicRoute } from '../router/router.selectors';

// Import infrastructure and domain services
import { AmplifyService } from '@app/core/infrastructure/auth/amplify.service';
import { ClockSkewService, DeviceClockSkewError, DeviceClockVerificationError } from '@app/core/infrastructure/auth/clock-skew.service';
import { UserApiService } from '@app/core/api/user';
import { AvatarFacade } from '@app/store/avatar/avatar.facade';
import { NotificationFacade } from '@app/store/notification';
import { StripeFacade } from '@app/store/stripe';
import { LoggerService, ToastNotificationService, MetaAnalyticsService } from '@app/core/infrastructure';
import { UserData } from './models/auth.models';
import { UserActions } from '../user/user.actions';
import { UserPreferencesActions } from '../user-preferences/user-preferences.actions';
import { UserSettingsActions } from '../user-settings/user-settings.actions';
import { AudioActions } from '../audio/audio.actions';
import { AvatarActions } from '../avatar/avatar.actions';
import { LanguageActions } from '../language/language.actions';
import { MatchingActions } from '../matching/matching.actions';
import { Capacitor } from '@capacitor/core';
import { PlatformType } from '@app/core/api/notification';
import { UserPreferencesApiService } from '@app/core/api/user-preferences';
import { ConsentApiService, CreateConsentRequest } from '@app/core/api/consent';
import { CONSENT_STORAGE_KEY, ConsentRecord } from '@app/pages/welcome-consent/welcome-consent.page';
import { POLICY_VERSIONS } from '@app/core/constants/policy-versions.constants';

@Injectable()
export class AuthEffects {
  // Use inject() for class field initialization (Angular 14+ pattern)
  // This ensures dependencies are available when effects are created
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);
  private readonly authAmplify = inject(AmplifyService);
  private readonly clockSkewService = inject(ClockSkewService);
  private readonly userApiService = inject(UserApiService);
  private readonly avatarFacade = inject(AvatarFacade);
  private readonly notificationFacade = inject(NotificationFacade);
  private readonly stripeFacade = inject(StripeFacade);
  private readonly logger = inject(LoggerService).scope('AuthEffects');
  private readonly toastNotification = inject(ToastNotificationService);
  private readonly ngZone = inject(NgZone);
  private readonly translate = inject(TranslateService);
  private readonly metaAnalytics = inject(MetaAnalyticsService);
  private readonly userPreferencesApi = inject(UserPreferencesApiService);
  private readonly consentApi = inject(ConsentApiService);
  
  /** Keys to preserve across localStorage.clear() during logout */
  private static readonly PRESERVED_STORAGE_KEYS = [CONSENT_STORAGE_KEY];

  constructor() {
    this.setupHubListener();
  }

  /**
   * Clear all browser storage (localStorage, sessionStorage)
   * Used during logout and auth failures.
   * Preserves consent-related keys so the welcome screen is not shown again.
   */
  private clearAllStorage(): void {
    try {
      // Preserve consent keys across logout
      const preserved = AuthEffects.PRESERVED_STORAGE_KEYS.map(
        key => [key, localStorage.getItem(key)] as const
      );
      localStorage.clear();
      sessionStorage.clear();
      // Restore preserved keys
      for (const [key, value] of preserved) {
        if (value !== null) {
          localStorage.setItem(key, value);
        }
      }
    } catch (e) {
      this.logger.error('Error clearing browser storage:', e);
    }
  }

  /**
   * Check if error is an authentication/authorization error
   * These errors indicate the token is invalid/revoked and user should be logged out
   */
  private isAuthenticationError(error: any): boolean {
    const errorName = error?.name || '';
    const errorCode = error?.code || '';
    const errorMessage = error?.message || '';
    
    // Check for common authentication error patterns
    return (
      errorName === 'NotAuthorizedException' ||
      errorCode === 'NotAuthorizedException' ||
      errorMessage.includes('Access Token has been revoked') ||
      errorMessage.includes('Invalid Access Token') ||
      errorMessage.includes('Access token is expired') ||
      errorMessage.includes('Token is expired')
    );
  }

  private hasTrackingConsentGranted(): boolean {
    const rawConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!rawConsent) {
      return false;
    }

    try {
      const consentRecord = JSON.parse(rawConsent) as ConsentRecord;
      return consentRecord.trackingConsent === true;
    } catch {
      return false;
    }
  }

  /**
   * Setup Amplify Hub listener for auth events
   */
  private setupHubListener(): void {
    Hub.listen('auth', async (capsule: HubCapsule<string, HubPayload>) => {
      const { payload } = capsule;
      const userFromHub = payload.data as AuthUser;
      
      this.logger.debug(`Hub event: ${payload.event}`, 
        payload.event === 'signedIn' || payload.event === 'autoSignIn' ? `(User: ${userFromHub?.userId})` : '');

      // Run all store dispatches inside NgZone to ensure proper change detection
      this.ngZone.run(() => {
        switch (payload.event) {
          case 'signedIn':
            this.store.dispatch(AuthActions.hubSignedIn({ userId: userFromHub?.userId || '' }));
            break;
          case 'autoSignIn':
            this.store.dispatch(AuthActions.hubAutoSignIn({ userId: userFromHub?.userId || '' }));
            break;
          case 'signedOut':
            this.store.dispatch(AuthActions.hubSignedOut());
            break;
          case 'signInWithRedirect_failure':
          case 'autoSignIn_failure':
            this.logger.error('Hub Sign in failure:', payload.data);
            const errorData = payload.data as any;
            this.store.dispatch(AuthActions.hubSignInFailure({ 
              error: { 
                message: errorData?.message || 'Authentication failed',
                code: errorData?.code 
              } 
            }));
            break;
        }
      });
    });
  }

  /**
   * Check initial authentication status
   */
  checkInitialAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkInitialAuth),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectInitialAuthCheckCompleted)
      ),
      exhaustMap(([action, isAuthenticated, checkCompleted]) => {
        // Skip if already authenticated and check completed (prevents duplicate auth flows)
        if (isAuthenticated && checkCompleted) {
          this.logger.debug('Auth already completed, skipping duplicate check');
          return of(AuthActions.initialAuthCheckCompleted());
        }

        // Give Amplify more time to initialize after OAuth token exchange
        // OAuth flows need additional time for session establishment
        return timer(500).pipe(
          switchMap(() => this.authAmplify.getCurrentUser()),
          switchMap(user => {
            if (user && user.userId) {
              this.logger.debug('User authenticated, fetching attributes');
              // User is authenticated - fetch attributes
              return this.authAmplify.fetchUserAttributes().pipe(
                map(attributes => {
                  const userData: UserData = { 
                    ...attributes, 
                    sub: attributes.sub || user.userId,
                    email: attributes.email || ''
                  };
                  return AuthActions.initialAuthCheckSuccess({ 
                    user: userData, 
                    isAuthenticated: true 
                  });
                }),
                catchError(error => {
                  this.logger.error('Error fetching user attributes:', error);
                  
                  // Check if this is a revoked/invalid token error
                  if (this.isAuthenticationError(error)) {
                    this.logger.warn('Token revoked or invalid - triggering logout');
                    // Logout will handle cleanup and redirect to login
                    return of(AuthActions.logout({}));
                  }
                  
                  // For other errors, just treat as not authenticated
                  this.logger.debug('Auth check failed, treating as not authenticated');
                  return of(AuthActions.initialAuthCheckSuccess({ 
                    user: null, 
                    isAuthenticated: false 
                  }));
                })
              );
            } else {
              this.logger.debug('No authenticated user found during initial check');
              return of(AuthActions.initialAuthCheckSuccess({ 
                user: null, 
                isAuthenticated: false 
              }));
            }
          }),
          catchError(error => {
            this.logger.debug('Error during initial auth check:', error.message || error);
            // Don't treat as failure - just means user isn't logged in
            return of(AuthActions.initialAuthCheckSuccess({ 
              user: null, 
              isAuthenticated: false 
            }));
          })
        );
      })
    )
  );

  /**
   * Complete initial auth check and start token monitoring if authenticated
   */
  initialAuthCheckSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initialAuthCheckSuccess),
      tap(({ user, isAuthenticated }) => {
        if (isAuthenticated && user) {
          // Start token monitoring
          this.authAmplify.startTokenExpiryMonitoring(() => {
            this.ngZone.run(() => {
              this.store.dispatch(AuthActions.logout({}));
            });
          });
          
          // Notify stripe service
          this.stripeFacade.setAuthenticationState(true);
          
          // Trigger backend sync
          this.store.dispatch(AuthActions.syncUserWithBackend({ 
            userId: user.sub || '', 
            userAttributes: user 
          }));
        }
      }),
      map(() => AuthActions.initialAuthCheckCompleted())
    )
  );

  /**
   * Handle login
   */
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      exhaustMap(({ credentials }) =>
        this.authAmplify.signIn(credentials.email, credentials.password).pipe(
          mergeMap((output) => {
            if (output.nextStep.signInStep === 'DONE') {
              return this.authAmplify.getCurrentUser().pipe(
                switchMap(user => this.authAmplify.fetchUserAttributes().pipe(
                  map(attributes => {
                    const userData: UserData = { 
                      ...attributes, 
                      sub: attributes.sub || user.userId,
                      email: attributes.email || ''
                    };
                    return AuthActions.loginSuccess({ user: userData });
                  })
                ))
              );
            } else if (output.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
              return of(AuthActions.loginFailure({ 
                error: { 
                  message: 'CONFIRM_SIGN_UP: Please confirm your email address',
                  code: 'CONFIRM_SIGN_UP'
                } 
              }));
            } else {
              return of(AuthActions.loginFailure({ 
                error: { message: `Sign in incomplete: ${output.nextStep.signInStep}` } 
              }));
            }
          }),
          catchError(error => {
            this.logger.error('Login error:', error);
            return of(AuthActions.loginFailure({ 
              error: { 
                message: error.message || 'Login failed',
                code: error.code,
                name: error.name
              } 
            }));
          })
        )
      )
    )
  );

  /**
   * Handle successful login - sync with backend
   */
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ user }) => {
        // Start token monitoring
        this.authAmplify.startTokenExpiryMonitoring(() => {
          this.ngZone.run(() => {
            this.store.dispatch(AuthActions.logout({}));
          });
        });
        
        // Notify stripe service
        this.stripeFacade.setAuthenticationState(true);
        
        // Track login event for Meta attribution (production only)
        const userId = user.sub || '';
        const email = user.email;
        if (this.hasTrackingConsentGranted()) {
          this.metaAnalytics.trackLogin(userId, email);
        }
      }),
      map(({ user }) => AuthActions.syncUserWithBackend({ 
        userId: user.sub || '', 
        userAttributes: user 
      }))
    )
  );

  /**
   * Handle OAuth login
   */
  oAuthLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.oAuthLogin),
      exhaustMap(({ provider }) =>
        this.clockSkewService.verifyDeviceClockForAuth().pipe(
          switchMap(() => this.authAmplify.signInWithRedirect(provider)),
          map(() => AuthActions.oAuthProcessingStarted()),
          catchError(error => {
            this.logger.error('OAuth login error:', error);

            if (error instanceof DeviceClockSkewError || error instanceof DeviceClockVerificationError) {
              const messageKey = error instanceof DeviceClockVerificationError
                ? 'toast.deviceClockVerificationFailed'
                : 'toast.deviceClockSkew';
              const message = this.translate.instant(messageKey);
              return of(AuthActions.oAuthLoginFailure({
                error: {
                  message,
                  code: error.code,
                  name: error.name
                }
              }));
            }

            return of(AuthActions.oAuthLoginFailure({ 
              error: { 
                message: error.message || 'OAuth login failed',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  /**
   * Handle Hub signedIn event - only for OAuth/external sign-ins
   * Skip if user is already authenticated (email login already handled it)
   */
  hubSignedIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.hubSignedIn, AuthActions.hubAutoSignIn),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectLastProcessedUserId)
      ),
      filter(([action, isAuthenticated, lastProcessedUserId]) => {
        // Skip if already authenticated - email login already handled this
        if (isAuthenticated) {
          this.logger.debug('Skipping Hub event - already authenticated');
          return false;
        }
        // Skip if same user already being processed
        if (lastProcessedUserId === action.userId) {
          this.logger.debug('Skipping Hub event - user already processed:', action.userId);
          return false;
        }
        return true;
      }),
      exhaustMap(([action]) =>
        this.authAmplify.fetchUserAttributes().pipe(
          map(attributes => {
            const userData: UserData = { 
              ...attributes,
              sub: attributes.sub || action.userId,
              email: attributes.email || ''
            };
            return AuthActions.loginSuccess({ user: userData });
          }),
          catchError(error => {
            this.logger.error('Error processing Hub signedIn:', error);
            
            // Check if this is a revoked/invalid token error
            if (this.isAuthenticationError(error)) {
              this.logger.warn('Token revoked or invalid during Hub signedIn - triggering logout');
              return of(AuthActions.logout({}));
            }
            
            return of(AuthActions.hubSignInFailure({ 
              error: { message: error.message || 'Failed to fetch user data' } 
            }));
          })
        )
      )
    )
  );

  /**
   * Sync user with backend
   */
  syncUserWithBackend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncUserWithBackend),
      exhaustMap(({ userId, userAttributes }) =>
        this.authAmplify.getIdToken().pipe(
          take(1),
          switchMap((token: string | null) => {
            if (!token) {
              throw new Error('No valid token available');
            }
            this.logger.debug('Syncing user with backend:', userId);
            return this.userApiService.createOrSyncMe();
          }),
          map(userProfile => {
            this.logger.debug('Backend user synced successfully');
            
            // Dispatch action to update user state
            this.store.dispatch(UserActions.setProfile({ profile: userProfile }));
            
            const userData: UserData = { 
              ...userAttributes, 
              sub: userAttributes.sub || userId,
              email: userAttributes.email || '',
              backendUserId: userProfile.id,
              syncedAt: new Date().toISOString()
            };
            return AuthActions.syncUserWithBackendSuccess({ user: userData });
          }),
          catchError(error => {
            this.logger.error('Backend sync error:', error);
            return of(AuthActions.syncUserWithBackendFailure({ 
              error: { message: error.message || 'Backend sync failed', code: error.code } 
            }));
          })
        )
      )
    )
  );

  /**
   * After successful backend sync, register push notifications and initialize avatar
   * Waits for avatar initialization before signaling completion to ensure proper navigation
   */
  afterBackendSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncUserWithBackendSuccess),
      withLatestFrom(
        this.notificationFacade.tokenValue$,
        this.notificationFacade.permissionStatus$
      ),
      tap(([{ user }, token]) => {
        this.logger.debug('Post-sync operations for user:', user.sub);

        // Load user preferences and settings from new endpoints
        this.store.dispatch(UserPreferencesActions.loadPreferences());
        this.store.dispatch(UserSettingsActions.loadSettings());

        // Register push notification token if available
        if (token) {
          const platform = (Capacitor.isNativePlatform() 
            ? Capacitor.getPlatform() 
            : 'web') as PlatformType;
          this.notificationFacade.registerToken(token, platform);
        }

        // Initialize avatar (dispatch action to NgRx store)
        this.avatarFacade.initializeAvatars();
      }),
      // Wait for avatar initialization to complete before signaling sync completion
      switchMap(() =>
        race(
          this.actions$.pipe(
            ofType(AvatarActions.initializeAvatarsSuccess, AvatarActions.initializeAvatarsFailure),
            take(1)
          ),
          timer(10000) // 10s timeout fallback
        ).pipe(
          map(() => AuthActions.userBackendSyncCompleted())
        )
      )
    )
  );

  /**
   * Sync user consents to the dedicated /consents/bulk API after every successful login.
   *
   * Why on every login (not just first)?
   * - The API is idempotent (upsert by consent_type) so repeated calls are safe.
   * - Ensures consent records exist even if a previous sync failed.
   * - Picks up updated policy versions if the app was upgraded between logins.
   *
   * Consent sources:
   * - Welcome-consent screen (native): trackingConsent, aiConsent → stored in localStorage pre-login
   * - Registration form (web + native): acceptTerms checkbox → implies terms_of_service + privacy_policy
   *
   * For OAuth / social logins the user bypasses the registration form, so we
   * always send terms_of_service + privacy_policy as "given" — the user agreed
   * to them implicitly by using the app (enforced by the welcome-consent guard on native
   * and the acceptTerms checkbox on web registration).
   */
  syncConsentToBackend$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncUserWithBackendSuccess),
      switchMap(() => {
        const consents = this.buildConsentPayload();

        if (consents.length === 0) {
          this.logger.debug('No consents to sync');
          return of(null);
        }

        this.logger.debug('Syncing consents to backend:', consents);

        return this.consentApi.createBulkConsents({ consents }).pipe(
          tap(() => this.logger.info('Consents synced to backend successfully')),
          catchError(error => {
            this.logger.error('Failed to sync consents to backend:', error);
            // Non-blocking — will retry on next login
            return of(null);
          })
        );
      })
    ),
    { dispatch: false }
  );

  /**
   * Build the array of consent requests from localStorage + defaults.
   *
   * Best-practice: always send at least terms_of_service and privacy_policy
   * with the current policy version. Additional consents (health_data_processing,
   * voice_storage_analytics) are derived from the welcome-consent screen record.
   */
  private buildConsentPayload(): CreateConsentRequest[] {
    const consents: CreateConsentRequest[] = [];

    // 1. Terms of Service — always "given" (required to use the app)
    consents.push({
      consent_type: 'terms_of_service',
      consent_status: 'given',
      policy_version: POLICY_VERSIONS.termsOfService,
    });

    // 2. Privacy Policy — always "given" (required to use the app)
    consents.push({
      consent_type: 'privacy_policy',
      consent_status: 'given',
      policy_version: POLICY_VERSIONS.privacyPolicy,
    });

    // 3. Derive optional consents from the welcome-consent screen record
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw) {
      try {
        const record: ConsentRecord = JSON.parse(raw);

        // AI / health-data processing consent
        if (record.aiConsent) {
          consents.push({
            consent_type: 'health_data_processing',
            consent_status: 'given',
          });
        }

        // Tracking / voice-storage-analytics consent
        if (record.trackingConsent) {
          consents.push({
            consent_type: 'voice_storage_analytics',
            consent_status: 'given',
          });
        }
      } catch (e) {
        this.logger.warn('Could not parse consent record from localStorage', e);
      }
    }

    return consents;
  }

  /**
   * Navigate after successful authentication and sync
   */
  navigateAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userBackendSyncCompleted),
      withLatestFrom(
        this.store.select(selectIsAuthenticated),
        this.store.select(selectIsPublicRoute),
        this.store.select(selectUrl)
      ),
      filter(([_, isAuthenticated]) => isAuthenticated),
      tap(([_, __, isPublicRoute, currentUrl]) => {
        const isOnRootRoute = currentUrl === '/' || currentUrl === '';
        const isOnPricingPage = currentUrl?.startsWith('/billing') || currentUrl?.startsWith('/plans');
        
        if ((isPublicRoute || isOnRootRoute) && !isOnPricingPage) {
          // Load subscription, then decide where to navigate
          this.stripeFacade.loadUserSubscription();
        }
      }),
      filter(([_, __, isPublicRoute, currentUrl]) => {
        const isOnRootRoute = currentUrl === '/' || currentUrl === '';
        const isOnPricingPage = currentUrl?.startsWith('/billing') || currentUrl?.startsWith('/plans');
        return (isPublicRoute || isOnRootRoute) && !isOnPricingPage;
      }),
      switchMap(() => 
        this.stripeFacade.userSubscription$.pipe(
          filter(subscription => subscription !== null),
          take(1),
          withLatestFrom(
            this.avatarFacade.activeAvatar$,
            this.store.select(selectUrl) // Get current URL at navigation time to prevent race condition
          ),
          tap(([subscription, avatar, currentUrl]) => {
            // IMPORTANT: Re-check URL at navigation time to prevent race condition
            // User might have navigated away (e.g., to /billing or /plans) while waiting for subscription data
            const isOnPricingPage = currentUrl?.startsWith('/billing') || currentUrl?.startsWith('/plans');
            if (isOnPricingPage) {
              this.logger.debug('User already on billing or plans page, skipping post-login navigation');
              return;
            }
            
            const hasMinutes = (subscription?.remaining_seconds ?? 0) > 0;
            const isPaidPlanActive = subscription?.has_subscription === true && subscription?.is_active === true;
            const isTrialPlan = subscription?.is_trial === true;
            const canUseSession = subscription?.can_use_service === true &&
                                  subscription?.is_expired !== true &&
                                  hasMinutes;
            const canAutoStart = !!avatar && canUseSession && (isPaidPlanActive || isTrialPlan);
            
            if (!avatar || !hasMinutes) {
              this.router.navigate(['/avatars']);
            } else if (canAutoStart) {
              this.router.navigate(['/home'], { state: { autoStart: true } });
            } else {
              this.router.navigate(['/home']);
            }
          })
        )
      )
    ),
    { dispatch: false }
  );

  /**
   * Handle logout
   * @param skipTokenUnregister - Skip token unregistration if already done (e.g., during account deletion)
   */
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      exhaustMap(({ skipTokenUnregister }) => {
        // Only unregister token if not already done
        if (!skipTokenUnregister) {
          const platform = (Capacitor.isNativePlatform() 
            ? Capacitor.getPlatform() 
            : 'web') as PlatformType;
          this.logger.debug('Unregistering push token during logout');
          this.notificationFacade.unregisterToken(platform, true);
        } else {
          this.logger.debug('Skipping token unregistration (already done)');
        }
        
        // Stop token monitoring
        this.authAmplify.stopTokenExpiryMonitoring();

        // Stop stripe auto-refresh IMMEDIATELY to prevent subscription requests
        // during the async signOut window (before logoutSuccess dispatches)
        this.stripeFacade.setAuthenticationState(false);

        // Cleanup via NgRx actions (replaces CleanupService)
        this.store.dispatch(AudioActions.stopSession({}));
        this.store.dispatch(AvatarActions.clearAvatars());
        this.store.dispatch(UserActions.clearProfile());
        this.store.dispatch(UserPreferencesActions.clearPreferences());
        this.store.dispatch(UserSettingsActions.clearSettings());
        this.store.dispatch(LanguageActions.resetLanguage());
        this.store.dispatch(MatchingActions.clearMatches());
        
        // Sign out from Amplify (this will also clear Cognito storage internally)
        return from(this.authAmplify.signOut()).pipe(
          tap(() => {
            // Clear all storage AFTER Amplify signOut completes
            // This ensures Amplify's cleanup happens first, then we do a final sweep
            this.clearAllStorage();
            this.logger.debug('Logout successful - all storage cleared');
          }),
          map(() => AuthActions.logoutSuccess()),
          catchError(error => {
            this.logger.error('Logout error:', error);
            // Still clear state even if signOut fails
            this.authAmplify.stopTokenExpiryMonitoring();
            this.clearAllStorage();
            return of(AuthActions.logoutSuccess());
          })
        );
      })
    )
  );

  /**
   * Navigate to login after logout
   * Keep loading state active until navigation completes to prevent content flash
   */
  navigateAfterLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutSuccess, AuthActions.hubSignedOut),
      switchMap(() => {
        // Clear user profile, preferences and settings state
        this.store.dispatch(UserActions.clearProfile());
        this.store.dispatch(UserPreferencesActions.clearPreferences());
        this.store.dispatch(UserSettingsActions.clearSettings());
        
        // Notify stripe service
        this.stripeFacade.setAuthenticationState(false);
        
        // Navigate to login and wait for it to complete
        return from(this.router.navigate(['/login'])).pipe(
          // After navigation completes, reset the auth state to clear loading
          map(() => AuthActions.resetAuthState())
        );
      })
    )
  );

  /**
   * Handle Hub signed out
   */
  hubSignedOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.hubSignedOut),
      tap(() => {
        this.authAmplify.stopTokenExpiryMonitoring();
        this.clearAllStorage();

        // Cleanup via NgRx actions (replaces CleanupService)
        this.store.dispatch(AudioActions.stopSession({}));
        this.store.dispatch(AvatarActions.clearAvatars());
        this.store.dispatch(UserActions.clearProfile());
        this.store.dispatch(UserPreferencesActions.clearPreferences());
        this.store.dispatch(UserSettingsActions.clearSettings());
        this.store.dispatch(MatchingActions.clearMatches());
      }),
      map(() => AuthActions.resetAuthState())
    )
  );

  /**
   * Handle password change
   */
  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      exhaustMap(({ oldPassword, newPassword }) =>
        this.authAmplify.updatePassword(oldPassword, newPassword).pipe(
          map(() => {
            this.logger.debug('Password changed successfully');
            return AuthActions.changePasswordSuccess();
          }),
          catchError(error => {
            this.logger.error('Password change failed:', error);

            // Translate Cognito errors into user-friendly messages so the raw
            // regex/parameter detail (e.g. "^[\S]+.*[\S]+$") never reaches the
            // UI. Mirror of the same approach used in confirmPasswordReset$.
            const code = error.code || error.name;
            let message = error.message || 'Failed to change password';

            if (code === 'NotAuthorizedException') {
              message = 'Your current password is incorrect.';
            } else if (code === 'InvalidPasswordException') {
              message = 'New password does not meet the password requirements.';
            } else if (code === 'InvalidParameterException') {
              message = 'Password format is invalid. Please check both fields and try again.';
            } else if (code === 'LimitExceededException') {
              message = 'Too many attempts. Please try again later.';
            }

            return of(AuthActions.changePasswordFailure({
              error: {
                message,
                code: error.code
              }
            }));
          })
        )
      )
    )
  );

  /**
   * Handle registration
   */
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      exhaustMap(({ data }) =>
        this.authAmplify.signUp(data.email, data.password, data.email).pipe(
          map(() => {
            this.logger.debug('Registration successful');
            return AuthActions.registerSuccess({ email: data.email });
          }),
          catchError(error => {
            this.logger.error('Registration error:', error);
            
            // Provide user-friendly error messages
            let errorMessage = error.message || 'Registration failed';
            
            if (error.name === 'UsernameExistsException' || error.code === 'UsernameExistsException') {
              errorMessage = 'This email is already registered. If you signed up with Google or Facebook, please use that login method instead. Otherwise, try logging in or use the "Forgot Password" option.';
            }
            
            return of(AuthActions.registerFailure({ 
              error: { 
                message: errorMessage,
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  /**
   * Navigate to confirm email after registration
   */
  navigateAfterRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerSuccess),
      tap(({ email }) => {
        this.router.navigate(['/confirm-email'], { queryParams: { email } });
      })
    ),
    { dispatch: false }
  );

  /**
   * Handle email confirmation
   */
  confirmEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmEmail),
      exhaustMap(({ email, code }) =>
        this.authAmplify.confirmSignUp(email, code).pipe(
          map(() => {
            this.logger.debug('Email confirmed successfully');
            return AuthActions.confirmEmailSuccess({ email });
          }),
          catchError(error => {
            this.logger.error('Email confirmation error:', error);
            return of(AuthActions.confirmEmailFailure({ 
              error: { 
                message: error.message || 'Email confirmation failed',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  /**
   * Navigate to login after successful email confirmation
   */
  navigateAfterEmailConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmEmailSuccess),
      tap(() => {
        this.router.navigate(['/login']);
      })
    ),
    { dispatch: false }
  );

  /**
   * Handle resend confirmation code
   */
  resendConfirmationCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resendConfirmationCode),
      exhaustMap(({ email }) =>
        this.authAmplify.resendSignUpCode(email).pipe(
          map(() => {
            this.logger.debug('Confirmation code resent successfully');
            this.toastNotification.showSuccess(
              'Verification code sent! Please check your email inbox and spam folder.'
            );
            return AuthActions.resendConfirmationCodeSuccess();
          }),
          catchError(error => {
            this.logger.error('Resend confirmation code error:', error);
            return of(AuthActions.resendConfirmationCodeFailure({ 
              error: { 
                message: error.message || 'Failed to resend confirmation code',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  /**
   * Handle password reset request
   */
  requestPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestPasswordReset),
      exhaustMap(({ email }) =>
        this.authAmplify.resetPassword(email).pipe(
          map(() => {
            this.logger.debug('Password reset code sent successfully');
            return AuthActions.requestPasswordResetSuccess({ email });
          }),
          catchError(error => {
            this.logger.error('Password reset request error:', error);
            return of(AuthActions.requestPasswordResetFailure({ 
              error: { 
                message: error.message || 'Failed to request password reset',
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  /**
   * Navigate to confirm reset password after successful reset request
   * Show informative message that doesn't reveal if email exists (security best practice)
   */
  navigateAfterPasswordResetRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.requestPasswordResetSuccess),
      tap(({ email }) => {
        this.toastNotification.showSuccess(
          'If an account exists with this email, you will receive a password reset code. Please check your inbox and spam folder.'
        );
        this.router.navigate(['/confirm-reset-password'], { 
          queryParams: { email } 
        });
      })
    ),
    { dispatch: false }
  );

  /**
   * Handle confirm password reset
   */
  confirmPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmPasswordReset),
      exhaustMap(({ email, code, newPassword }) =>
        this.authAmplify.confirmResetPassword(email, code, newPassword).pipe(
          map(() => {
            this.logger.debug('Password reset confirmed successfully');
            return AuthActions.confirmPasswordResetSuccess();
          }),
          catchError(error => {
            this.logger.error('Confirm password reset error:', error);
            
            // Provide helpful error messages
            let message = error.message || 'Failed to confirm password reset';
            
            if (error.code === 'CodeMismatchException') {
              message = 'Invalid verification code. Please check and try again, or request a new code.';
            } else if (error.code === 'ExpiredCodeException') {
              message = 'Verification code has expired. Please request a new code.';
            } else if (error.code === 'LimitExceededException') {
              message = 'Too many attempts. Please try again later.';
            } else if (error.code === 'UserNotFoundException' || error.name === 'UserNotFoundException') {
              message = 'Account not found. Please check your email address or sign up for a new account.';
            }
            
            return of(AuthActions.confirmPasswordResetFailure({ 
              error: { 
                message,
                code: error.code 
              } 
            }));
          })
        )
      )
    )
  );

  /**
   * Navigate to login after successful password reset confirmation
   */
  navigateAfterPasswordResetConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmPasswordResetSuccess),
      tap(() => {
        this.toastNotification.showSuccess(this.translate.instant('toast.passwordResetSuccess'));
        this.router.navigate(['/login']);
      })
    ),
    { dispatch: false }
  );

  /**
   * Handle backend sync failure
   * Shows error toast and logs the user out
   */
  handleBackendSyncFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncUserWithBackendFailure),
      tap(({ error }) => {
        this.toastNotification.showError(
          this.translate.instant('toast.signInFailed')
        );
      }),
      map(() => AuthActions.logout({}))
    )
  );

  /**
   * Logout after successful account deletion
   * Token is already unregistered before account deletion, so we skip it here
   */
  logoutAfterAccountDeletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteAccountSuccess),
      map(() => {
        this.logger.debug('Account deleted successfully, triggering logout (skipping token unregister)');
        return AuthActions.logout({ skipTokenUnregister: true });
      })
    )
  );
}
